import { useState } from 'react';
import { Link } from 'react-router-dom';

import apiToken, { handleApiError } from '@/shared/services/axiosInstance';
import { NavProps } from '@/shared/types/NavTypes';
import Button from '@/shared/components/UI/Button';
import LinkButton from '@/shared/components/UI/LinkButton';

import NavInner from '../NavInner';

const Nav = ({ categoryData, artistCategoryData }: NavProps) => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [navChildren, setNavChildren] = useState<Array<any>>([]);
  const [currentCategoryName, setCurrentCategoryName] = useState<string>('');

  const getArtistCategory = async (artistId: number) => {
    try {
      const response = await apiToken.get(`/api/v1/artist/list/${artistId}`);
      setNavChildren(response.data.data.artistList);
      setIsNavOpen(true);
    } catch (error) {
      const errorMessage = handleApiError(error);
      console.error('Failed to fetch artist category:', errorMessage);
    }
  };

  return (
    <>
      {!isNavOpen && (
        <div className="absolute left-1/2 z-20 flex w-[1024px] -translate-x-1/2 justify-center rounded-b-2xl border-t-[1px] bg-white px-0 py-8">
          <ul className="flex w-full flex-col gap-2">
            <li className="flex h-9 items-center justify-center">
              <LinkButton
                to="/productlist/0?tag=new&categoryName=NEW"
                variant="text"
                className="text-[0.625rem] font-semibold tracking-[0.0625rem] text-[#666] hover:text-[#666]"
              >
                NEW
              </LinkButton>
            </li>
            <li className="flex h-9 items-center justify-center">
              <LinkButton
                to="/productlist/0?tag=pre&categoryName=PRE-ORDER"
                variant="text"
                className="text-[0.625rem] font-semibold tracking-[0.0625rem] text-[#666] hover:text-[#666]"
              >
                PRE-ORDER
              </LinkButton>
            </li>
          </ul>
          <ul className="flex w-full flex-col gap-2">
            {artistCategoryData?.map((artist) => (
              <li
                key={artist.id}
                className="flex h-9 items-center justify-center"
              >
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    getArtistCategory(artist.id);
                    setCurrentCategoryName(artist.category);
                  }}
                  className="h-auto text-[0.625rem] font-semibold tracking-[0.0625rem] text-[#666]"
                >
                  {artist.category}
                </Button>
              </li>
            ))}
          </ul>
          <ul className="flex w-full flex-col gap-2">
            {categoryData
              ?.filter((category) => category.category === 'K-ALBUM')
              .flatMap((category) =>
                category.children?.map((child) => (
                  <li
                    key={child.id}
                    className="flex h-9 cursor-pointer items-center justify-center text-[0.625rem] font-semibold tracking-[0.0625rem] text-[#666]"
                  >
                    <Link
                      to={`/productlist/${child.id}?categoryName=${child.category}`}
                      className="no-underline"
                    >
                      {child.category}
                    </Link>
                  </li>
                )),
              )}
          </ul>
          <ul className="flex w-full flex-col gap-2">
            <li className="flex h-9 items-center justify-center">
              <LinkButton
                to="/productlist/9?categoryName=MERCHANDISE"
                variant="text"
                className="text-[0.625rem] font-semibold tracking-[0.0625rem] text-[#666]"
              >
                MERCHANDISE
              </LinkButton>
            </li>
            <li className="flex h-9 items-center justify-center">
              <LinkButton
                to="/productlist/10?categoryName=LIGHT STICK"
                variant="text"
                className="text-[0.625rem] font-semibold tracking-[0.0625rem] text-[#666]"
              >
                LIGHT STICK
              </LinkButton>
            </li>
            <li className="flex h-9 items-center justify-center">
              <LinkButton
                to="/productlist/13?categoryName=PHOTO BOOK"
                variant="text"
                className="text-[0.625rem] font-semibold tracking-[0.0625rem] text-[#666]"
              >
                PHOTO BOOK
              </LinkButton>
            </li>
            <li className="flex h-9 items-center justify-center">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  const merchCategory = categoryData?.find(
                    (cat) => cat.category === 'K-MERCH',
                  );
                  const seasonCategory = merchCategory?.children?.find(
                    (child) => child.category === "SEASON'S GREETINGS",
                  );
                  setCurrentCategoryName(seasonCategory?.category || '');
                  setNavChildren(seasonCategory?.children || []);
                  setIsNavOpen(true);
                }}
                className="h-auto text-[0.625rem] font-semibold tracking-[0.0625rem] text-[#666]"
              >
                SEASON&apos;S GREETINGS
              </Button>
            </li>
            <li className="flex h-9 items-center justify-center">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  const merchCategory = categoryData?.find(
                    (cat) => cat.category === 'K-MERCH',
                  );
                  const characters = merchCategory?.children?.find(
                    (child) => child.category === 'K-CHARACTERS',
                  );
                  setCurrentCategoryName(characters?.category || '');
                  setNavChildren(characters?.children || []);
                  setIsNavOpen(true);
                }}
                className="h-auto text-[0.625rem] font-semibold tracking-[0.0625rem] text-[#666]"
              >
                K-CHARACTERS
              </Button>
            </li>
          </ul>
          <ul className="flex w-full flex-col gap-2">
            {categoryData
              ?.filter((category) => category.category === 'K-MAGAZINE')
              .map((category) => (
                <li
                  key={category.id}
                  className="flex h-9 items-center justify-center"
                >
                  {!category.children || category.children.length === 0 ? (
                    <LinkButton
                      to={`/productlist/${category.id}?categoryName=${category.category}`}
                      variant="text"
                      className="h-full w-full text-[0.625rem] font-semibold tracking-[0.0625rem] text-[#666]"
                    >
                      {category.category}
                    </LinkButton>
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setCurrentCategoryName(category.category);
                        setNavChildren(category.children || []);
                        setIsNavOpen(true);
                      }}
                      className="h-auto w-full text-[0.625rem] font-semibold tracking-[0.0625rem] text-[#666]"
                    >
                      {category.category}
                    </Button>
                  )}
                </li>
              ))}
          </ul>
          <ul className="flex w-full flex-col gap-2">
            <li className="flex h-9 items-center justify-center">
              <LinkButton
                to="/productlist/4?categoryName=ALL EVENTS"
                variant="text"
                className="text-[0.625rem] font-semibold tracking-[0.0625rem] text-[#666]"
              >
                ALL EVENTS
              </LinkButton>
            </li>
          </ul>
          <ul className="flex w-full flex-col gap-2">
            <li className="flex h-9 items-center justify-center">
              <LinkButton
                to="/contact"
                variant="text"
                className="text-[0.625rem] font-semibold tracking-[0.0625rem] text-[#666]"
              >
                CONTACT US
              </LinkButton>
            </li>
          </ul>
        </div>
      )}
      {isNavOpen && navChildren && (
        <NavInner
          title={currentCategoryName}
          setIsNavOpen={setIsNavOpen}
          childrenData={navChildren}
        />
      )}
    </>
  );
};
export default Nav;
